import {
  Box,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { PlayArrow } from "@mui/icons-material";
import "../../styles/BulkTagMappingLandingPage.css";

/**
 * Bulk Assignment Panel component for selecting a UD and applying it to selected tags.
 *
 * @param {Object} props - Component props
 * @param {Array} props.universalDescriptors - Array of available Universal Descriptors
 * @param {Object|null} props.bulkAssignmentUD - Currently selected Universal Descriptor for bulk assignment
 * @param {Function} props.onUDChange - Callback when UD selection changes
 * @param {number} props.selectedRowsCount - Number of selected rows
 * @param {number} props.selectedRowsSize - Size of selected rows set (for display)
 * @param {boolean} props.isApplying - Whether bulk assignment is in progress
 * @param {Function} props.onApply - Callback when Apply button is clicked
 */
const BulkAssignmentPanel = ({
  universalDescriptors,
  bulkAssignmentUD,
  onUDChange,
  selectedRowsCount,
  selectedRowsSize,
  isApplying,
  onApply,
}) => {
  return (
    <Box className='bulk-tag-mapping-assignment-box'>
      <Stack direction='row' alignItems='center' spacing={1}>
        <PlayArrow sx={{ color: "#017dba" }} />
        <Typography variant='h6' className='bulk-tag-mapping-assignment-header'>
          Bulk Assignment
        </Typography>
      </Stack>
      <Typography variant='h10' gutterBottom>
        Step 1: Select a Universal Descriptor below. Step 2: Check tags in the
        grid to assign it to. Step 3: Click "Apply to X Tags" button.
      </Typography>
      <Stack direction='row' spacing={2} alignItems='center' sx={{ mt: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Autocomplete
            fullWidth
            size='small'
            options={universalDescriptors.filter((ud) => ud.IsActive)}
            getOptionLabel={(option) =>
              `${option.TagName || ""} - ${option.UD || ""}`
            }
            value={bulkAssignmentUD}
            onChange={onUDChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Universal Descriptor'
                variant='outlined'
                placeholder='Select Universal Descriptor...'
              />
            )}
            isOptionEqualToValue={(option, value) =>
              option?.UniversalDescriptorId === value?.UniversalDescriptorId
            }
            filterOptions={(options, { inputValue }) => {
              const searchLower = inputValue.toLowerCase();
              return options.filter(
                (option) =>
                  (option.TagName || "").toLowerCase().includes(searchLower) ||
                  (option.UD || "").toLowerCase().includes(searchLower)
              );
            }}
          />
        </Box>
        <Box className='bulk-tag-mapping-selected-count-box'>
          <Typography
            variant='h4'
            className='bulk-tag-mapping-selected-count-number'>
            {selectedRowsSize.toLocaleString()}
          </Typography>
          <Typography
            variant='caption'
            className='bulk-tag-mapping-selected-count-label'>
            Tag Names Selected
          </Typography>
        </Box>
        <Button
          variant='contained'
          startIcon={
            isApplying ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              <PlayArrow />
            )
          }
          onClick={onApply}
          disabled={!bulkAssignmentUD || selectedRowsSize === 0 || isApplying}
          sx={{ minWidth: "180px" }}>
          {isApplying
            ? `Applying to ${selectedRowsCount.toLocaleString()} Tags`
            : `Apply to ${selectedRowsCount.toLocaleString()} Tags`}
        </Button>
      </Stack>
    </Box>
  );
};

export default BulkAssignmentPanel;
